/**
 * QuotePrintView.jsx — 견적서 인쇄/PDF 렌더링 컴포넌트
 *
 * 모던 미니멀 코퍼레이트 스타일의 A4 견적서 레이아웃입니다.
 * 브라우저 인쇄(@media print)와 html2canvas PDF 저장 양쪽에서 동일하게 사용됩니다.
 *
 * 구성:
 *   InfoRow        — (라벨 | 값) 테이블 행 헬퍼 컴포넌트
 *   QuotePrintView — 전체 견적서 레이아웃 (기본 export)
 *
 * Props (QuotePrintView):
 *   quote  {object}      — 견적서 데이터 (DB 레코드)
 *   stamp  {string|null} — 도장 이미지 base64 URL
 *   logo   {string|null} — 파인그룹 로고 base64 URL
 */
import React from 'react';
import { COMPANY, TERMS, parseFreeItems, parseComma, fmtKorDate, numToKoreanWon } from './constants';

// ── 폰트 / 색상 팔레트 ────────────────────────────────────────────────────────

/** 견적서 전용 폰트 스택 (한글 지원) */
const QF = '"Malgun Gothic","맑은 고딕","Apple SD Gothic Neo","Noto Sans KR",sans-serif';

/** 색상 팔레트 — 파인그룹 브랜드 블루 기반 */
const C = {
  blue:   '#1e40af',   // 메인 강조 (파인 브랜드 블루)
  navy:   '#1e3a8a',   // 제목 등 딥 네이비
  dark:   'var(--text)',   // 본문 텍스트
  gray:   'var(--text-muted)',   // 보조 텍스트
  light:  'var(--bg-subtle)',   // 연한 배경
  border: 'var(--border)',   // 테두리
  lBlue:  '#eff6ff',   // 합계금액 하이라이트 배경
  bBlue:  '#bfdbfe',   // 합계금액 하이라이트 테두리
};

// ── InfoRow 헬퍼 ─────────────────────────────────────────────────────────────

/**
 * (라벨 | 값) 한 행을 렌더링하는 테이블 행 컴포넌트.
 * valueNode prop으로 커스텀 React 노드(ex: 도장 오버레이)도 허용합니다.
 *
 * @param {string}        label     — 왼쪽 레이블 텍스트
 * @param {string}        [value]   — 오른쪽 값 텍스트
 * @param {ReactNode}     [valueNode] — 오른쪽 커스텀 노드 (value 대체)
 * @param {boolean}       [bold]    — 값 텍스트 굵게 표시 여부
 * @param {boolean}       [small]   — 값/레이블 폰트 크기 축소 (8px)
 */
function InfoRow({ label, value, valueNode, bold, small }) {
  return (
    <tr>
      <td style={{ fontSize: small ? 8 : 9, color: C.gray, paddingRight: 8, whiteSpace: 'nowrap', paddingBottom: 3, verticalAlign: 'top' }}>
        {label}
      </td>
      <td style={{ fontSize: small ? 8 : 9, color: C.dark, fontWeight: bold ? 700 : 400, paddingBottom: 3 }}>
        {valueNode ?? value}
      </td>
    </tr>
  );
}

// ── QuotePrintView 메인 컴포넌트 ─────────────────────────────────────────────

export default function QuotePrintView({ quote, stamp, logo }) {
  if (!quote) return null;

  // ── 금액 계산 ──
  const qty1    = quote.product1_qty        || 0;
  const price1  = quote.product1_unit_price || 0;
  const supply1 = qty1 * price1;
  const tax1    = quote.vat_yn ? Math.round(supply1 * 0.1) : 0;
  const qty2    = quote.product2_qty        || 0;
  const total   = supply1 + tax1;

  // ── 무상제공품목 텍스트 구성 ──
  // 수량이 1이면 이름만, 2 이상이면 "{홍보물} {수량}개" 형태로 나열
  const freeItems = parseFreeItems(quote.free_items);
  const freeText  = freeItems.filter((fi) => fi.name).map((fi) => {
    const q = parseComma(fi.qty) || 1;
    return q === 1 ? fi.name : `${fi.name} ${q}개`;
  }).join(',  ');

  // ── 빈 행 수 계산: A4 한 페이지 맞춤 (최소 4행) ──
  const usedRows   = (quote.product1_name ? 1 : 0) + (quote.product2_name ? 1 : 0);
  const emptyCount = Math.max(0, 4 - usedRows);

  // ── 공통 셀 스타일 팩토리 ──
  const th = (ex) => ({ padding: '5px 7px', fontSize: 9, fontWeight: 800, color: C.gray, border: `1px solid ${C.border}`, background: 'var(--bg-subtle)', ...ex });
  const td = (ex) => ({ padding: '5px 7px', fontSize: 10, color: C.dark, border: `1px solid ${C.border}`, ...ex });

  return (
    <div style={{
      fontFamily: QF, fontSize: 10, color: C.dark, lineHeight: 1.45,
      background: 'var(--bg-card)', padding: '90px 57px 45px 76px',
      boxSizing: 'border-box', width: '100%',
    }}>

      {/* ── 상단: 브랜드(좌) + 문서번호(우) ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        {/* 파인그룹 아이콘 로고 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          {logo
            ? <img src={logo} alt="파인그룹 로고" style={{ width: 30, height: 30, objectFit: 'contain', flexShrink: 0 }} />
            : <div style={{ width: 30, height: 30, borderRadius: 7, background: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 900, fontSize: 16, flexShrink: 0 }}>P</div>
          }
          <div>
            <div style={{ fontSize: 13, fontWeight: 900, color: C.blue, letterSpacing: 0.5 }}>PINE GROUP</div>
            <div style={{ fontSize: 8, color: C.gray, marginTop: 1 }}>㈜파인그룹</div>
          </div>
        </div>
        {/* 문서번호 */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 8, color: C.gray, letterSpacing: 0.5, marginBottom: 2 }}>문서번호</div>
          <div style={{ fontSize: 13, fontWeight: 900, color: C.blue }}>{quote.quote_number}</div>
        </div>
      </div>

      {/* ── 제목: border-bottom은 인쇄 시도 항상 출력됨 (background색은 인쇄 미출력) ── */}
      <div style={{ marginBottom: 18, paddingBottom: 8, borderBottom: `3px solid ${C.blue}` }}>
        <div style={{ fontSize: 27, fontWeight: 900, color: C.dark, letterSpacing: 9 }}>견  적  서</div>
      </div>

      {/* ── 공급받는 자 + 공급자 (좌우 패널) ── */}
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 8, marginBottom: 14 }}>
        <tbody>
          <tr>
            {/* 공급받는 자 패널 */}
            <td style={{ width: '42%', border: `1px solid ${C.border}`, borderRadius: 6, verticalAlign: 'top', padding: 0, overflow: 'hidden' }}>
              <div style={{ background: C.light, padding: '5px 10px', borderBottom: `1px solid ${C.border}`, fontSize: 9, fontWeight: 800, color: C.gray, letterSpacing: 1 }}>
                공급받는 자
              </div>
              <div style={{ padding: '10px 12px' }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: C.dark, marginBottom: 8 }}>
                  {quote.recipient_name}&nbsp;&nbsp;<span style={{ fontSize: 10, fontWeight: 500, color: C.gray }}>귀하</span>
                </div>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <tbody>
                    <InfoRow label="사업자번호" value={quote.business_number || ''} />
                    <InfoRow label="견  적  일" value={fmtKorDate(quote.issue_date)} />
                  </tbody>
                </table>
              </div>
            </td>

            {/* 공급자 패널 */}
            <td style={{ border: `1px solid ${C.border}`, borderRadius: 6, verticalAlign: 'top', padding: 0, overflow: 'hidden' }}>
              <div style={{ background: C.light, padding: '5px 10px', borderBottom: `1px solid ${C.border}`, fontSize: 9, fontWeight: 800, color: C.gray, letterSpacing: 1 }}>
                공&nbsp;&nbsp;급&nbsp;&nbsp;자
              </div>
              <div style={{ padding: '10px 12px' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <tbody>
                    <InfoRow label="사업자번호" value={COMPANY.bizNum} />
                    <InfoRow label="상&nbsp;&nbsp;&nbsp;호" value={COMPANY.name} bold />
                    {/* 대표자 — "(인)" 위에 도장 이미지를 절대 좌표로 겹침 */}
                    <InfoRow
                      label="대&nbsp;표&nbsp;자"
                      valueNode={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          정&nbsp;&nbsp;명&nbsp;&nbsp;철&nbsp;&nbsp;&nbsp;&nbsp;
                          <span style={{ position: 'relative', display: 'inline-block', lineHeight: 1 }}>
                            <span style={{ fontSize: 9 }}>(인)</span>
                            {stamp && (
                              <img
                                src={stamp}
                                alt="도장"
                                style={{
                                  position: 'absolute',
                                  top: '50%', left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  maxWidth: 52, maxHeight: 52,
                                  opacity: 0.88, pointerEvents: 'none',
                                }}
                              />
                            )}
                          </span>
                        </span>
                      }
                    />
                    <InfoRow label="소&nbsp;재&nbsp;지" value={COMPANY.address} small />
                    <InfoRow label="업태/업종" value={`${COMPANY.bizType}  /  ${COMPANY.bizKind}`} small />
                    <InfoRow label="연&nbsp;락&nbsp;처" value={COMPANY.phone} />
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── 합계금액 하이라이트 배너 ── */}
      <div style={{
        background: C.lBlue, border: `1px solid ${C.bBlue}`, borderRadius: 5,
        padding: '9px 16px', display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14,
      }}>
        <span style={{ fontSize: 9, fontWeight: 800, color: C.blue, letterSpacing: 1, flexShrink: 0 }}>합 계 금 액</span>
        <span style={{ fontSize: 14, fontWeight: 900, color: C.navy, flex: 1 }}>{numToKoreanWon(total)}</span>
        <span style={{ fontSize: 9, color: C.gray }}>( ₩</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: C.dark }}>{total.toLocaleString('ko-KR')}</span>
        <span style={{ fontSize: 9, color: C.gray }}>원 정 )</span>
      </div>

      {/* ── 품목 명세 테이블 ── */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: C.gray, letterSpacing: 1, marginBottom: 5 }}>품목 명세</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th({ textAlign: 'left', paddingLeft: 9 })}>품&nbsp;&nbsp;목&nbsp;&nbsp;명</th>
              <th style={th({ width: 52, textAlign: 'center', whiteSpace: 'nowrap' })}>장수</th>
              <th style={th({ width: 82, textAlign: 'right', paddingRight: 8 })}>단&nbsp;&nbsp;&nbsp;가</th>
              <th style={th({ width: 90, textAlign: 'right', paddingRight: 8 })}>공&nbsp;급&nbsp;가&nbsp;액</th>
              <th style={th({ width: 74, textAlign: 'right', paddingRight: 8 })}>세&nbsp;&nbsp;&nbsp;액</th>
              <th style={th({ width: 52, textAlign: 'center', whiteSpace: 'nowrap' })}>비고</th>
            </tr>
          </thead>
          <tbody>
            {/* 품목 1: 주 상품 */}
            {quote.product1_name && (
              <tr>
                <td style={td({ paddingLeft: 9 })}>{quote.product1_name}</td>
                <td style={td({ textAlign: 'center', whiteSpace: 'nowrap' })}>{qty1.toLocaleString('ko-KR')}</td>
                <td style={td({ textAlign: 'right', paddingRight: 8, whiteSpace: 'nowrap' })}>{price1.toLocaleString('ko-KR')}</td>
                <td style={td({ textAlign: 'right', paddingRight: 8, whiteSpace: 'nowrap' })}>{supply1.toLocaleString('ko-KR')}</td>
                <td style={td({ textAlign: 'right', paddingRight: 8, whiteSpace: 'nowrap' })}>{tax1.toLocaleString('ko-KR')}</td>
                <td style={td({ textAlign: 'center' })}></td>
              </tr>
            )}
            {/* 품목 2: 서비스 상품 (단가·공급가액·세액 없음) */}
            {quote.product2_name && (
              <tr>
                <td style={td({ paddingLeft: 9 })}>{quote.product2_name}</td>
                <td style={td({ textAlign: 'center', whiteSpace: 'nowrap' })}>{qty2.toLocaleString('ko-KR')}</td>
                <td style={td({ textAlign: 'center', color: 'var(--text-muted)' })}>-</td>
                <td style={td({ textAlign: 'center', color: 'var(--text-muted)' })}>-</td>
                <td style={td({ textAlign: 'center', color: 'var(--text-muted)' })}>-</td>
                <td style={td({ textAlign: 'center', fontSize: 9, color: C.blue, fontWeight: 700, whiteSpace: 'nowrap' })}>서비스</td>
              </tr>
            )}
            {/* 빈 행 (A4 맞춤) */}
            {Array.from({ length: emptyCount }).map((_, i) => (
              <tr key={i} style={{ height: 22 }}>
                {[0,1,2,3,4,5].map((c) => <td key={c} style={td({})}></td>)}
              </tr>
            ))}
            {/* 합계 행 */}
            <tr style={{ background: C.light }}>
              <td colSpan={2} style={td({ textAlign: 'center', fontWeight: 900, fontSize: 11, letterSpacing: 3, borderTop: `2px solid ${C.dark}` })}>계</td>
              <td style={td({ borderTop: `2px solid ${C.dark}` })}></td>
              <td style={td({ textAlign: 'right', paddingRight: 8, fontWeight: 800, fontSize: 11, borderTop: `2px solid ${C.dark}` })}>{supply1.toLocaleString('ko-KR')}</td>
              <td style={td({ textAlign: 'right', paddingRight: 8, fontWeight: 800, fontSize: 11, borderTop: `2px solid ${C.dark}` })}>{tax1.toLocaleString('ko-KR')}</td>
              <td style={td({ borderTop: `2px solid ${C.dark}` })}></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── 무상제공품목 + 비고 ── */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
        <tbody>
          {/* 무상제공품목이 있을 때만 표시 */}
          {freeText && (
            <tr>
              <td style={{ width: 90, background: C.light, border: `1px solid ${C.border}`, padding: '5px 10px', fontSize: 9, fontWeight: 800, color: C.gray, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                무상 제공품목
              </td>
              <td style={{ border: `1px solid ${C.border}`, padding: '5px 10px', fontSize: 10, minHeight: 18 }}>
                {freeText}
              </td>
            </tr>
          )}
          <tr>
            <td style={{ width: 90, background: C.light, border: `1px solid ${C.border}`, padding: '5px 10px', fontSize: 9, fontWeight: 800, color: C.gray, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
              비&nbsp;&nbsp;&nbsp;&nbsp;고
            </td>
            <td style={{ border: `1px solid ${C.border}`, padding: '5px 10px', fontSize: 10, minHeight: 22, whiteSpace: 'pre-wrap', color: '#333' }}>
              {quote.memo || ''}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── 중앙 인증 문구 ── */}
      <div style={{ textAlign: 'center', margin: '12px 0 10px' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.dark }}>상기와 같이 견적합니다.</div>
        <div style={{ fontSize: 10, color: C.gray, marginTop: 4 }}>{fmtKorDate(quote.issue_date)}</div>
      </div>

      {/* ── 입금 계좌 + 약관 ── */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 7, fontSize: 8.5, lineHeight: 1.65 }}>
        <div style={{ fontWeight: 800, fontSize: 9.5, color: C.blue, marginBottom: 3 }}>
          ■&nbsp;입금 계좌 : {COMPANY.bank}
        </div>
        {TERMS.map((term, i) => (
          <div key={i} style={{ color: 'var(--text-sub)' }}>
            <span style={{ fontWeight: 700, color: C.dark }}>{i + 1}.&nbsp;</span>{term}
          </div>
        ))}
      </div>
    </div>
  );
}
